export interface Query {
  // Core identifiers
  id: string;
  componentId: string;
  componentType: string;
  subId: number;
  queryLabel: string;
  originalQueryId: string;
  parentComponent: string;
  parentId: string;
  clusterId: string | null;
  clusterParent: string | null;
  neuronId: string;
  uniqueMainQueryIds: string;
  matchingFlag: string;

  // Query content
  decomposedQuery: string;
  originalQueryText: string;
  sqlDialect: string;
  queryWithoutJoin: string;
  queryWithoutCTE: string;
  queryWithoutTrailingAS: string;
  neuronText: string;

  // Project metadata
  projectId: string;
  projectName: string;
  author: string;
  role: string;

  // Execution context
  sessionId: string;
  warehouse: string;
  schema: string;
  database: string;
  startDate: string;
  endDate: string;
  RunTimestamp: string;
  queryStartTime: string;
  executionTime: number;
  queryStatus: string;
  querySequence: string;

  // Flags and modifications
  errorFlag: boolean;
  errorMessage: string;
  joinModifiedFlag: boolean;
  cteModifiedFlag: boolean;
  asModifiedFlag: boolean;
  llmFlag: boolean;

  // Structural components
  joins: string[];
  predicates: string[];
  inClauses: string[];
  whereClauses: string[];
  tableNames: string[];

  // Semantic enrichments
  features: string[];
  tags: string[];
  mentioned: string[];
  inferences: string[];
  values: string[];
  columnNames: string[];
  aliases: string[];
  medicationEnrichments: string[];
  indicationEnrichments: string[];
  procedureEnrichments: string[];

  // Execution metrics
  executions: string[];
  extraction?: number;
  // Descriptive metadata
  description: {
    main: string;
    inclusionRules: string;
    exclusionRules: string;
  };

  // Additional parameters
  unnamed01: string;
  unnamed0: string;
}

// Map component types to user-friendly names
export const mapQueryType = (componentType: string): string => {
  switch (componentType?.toUpperCase()) {
    case "MAIN":
      return "Original Query";
    case "CTE":
    case "FROM":
    case "JOIN":
      return "Extracted Neuron";
    default:
      return "Extracted Neuron";
  }
};

export const transformQueries = (data: any[]): Query[] => {
  return data.map((item) => ({
    // Core identifiers
    id: item.Component_ID || "",
    componentId: item.Component_ID || "",
    componentType: item.Query_Type || "MAIN",
    subId: Number(item.Unnamed_0) || 0,
    queryLabel:
      item.Query_Object_Flag === "1" ? "Flagged Query" : "Standard Query",
    originalQueryId: item.Original_Query_ID || "",
    parentComponent: item.Parent_Component_ID || "",
    parentId: item.Main_Query_ID || "",
    clusterId: item.Cluster_ID?.toString() || null,
    clusterParent: Array.isArray(item.Cluster_Parent)
      ? item.Cluster_Parent.join(", ")
      : "",
    neuronId: item.Neuron_ID || "",
    uniqueMainQueryIds: item.Unique_Main_Query_IDs || "",
    matchingFlag: item.Matching_Flag || "",
    extraction: Array.isArray(item.Number_of_Executions)
      ? item.Number_of_Executions.map(
          (str: string) => parseInt(str, 10) || 0
        ).reduce((sum: any, n: any) => sum + n, 0)
      : 0,
    // Query content
    decomposedQuery: item.Cleaned_SQL || "",
    originalQueryText: item.Query_Original_Text || "",
    sqlDialect: item.SQL_Dilect || "", // Note typo in source data
    queryWithoutJoin: item.Query_Without_Join || "",
    queryWithoutCTE: item.Query_Without_CTE || "",
    queryWithoutTrailingAS: item.Query_Without_Trailing_AS || "",
    neuronText: item.Neuron_Text || "",

    // Project metadata
    projectId: item.Project_ID || "",
    projectName: item.Project || "",
    author: item.Username || "",
    role: item.Role || "",

    // Execution context
    sessionId: item.Session_ID || "",
    warehouse: item.Warehouse || "",
    schema: item.Schema || "",
    database: item.Database || "",
    startDate: item.Query_Start_Date || "",
    RunTimestamp: item.Date_Timestamp || "",
    endDate: "2025-12-31", // From WHERE clause
    queryStartTime: item.Query_Start_Time || "",
    executionTime: Number(item.Query_Duration_seconds) || 0,
    queryStatus: item.Query_Status || "",
    querySequence: item.Query_Sequence || "",

    // Flags and modifications
    errorFlag: item.Has_Error === "True",
    errorMessage: [
      item.Preprocessing_Error_Message,
      item.Cleaning_Error_Message,
    ]
      .filter((msg) => msg)
      .join(" | "),
    joinModifiedFlag: item.Decomposition_Flag === "1",
    cteModifiedFlag: item.CTE_Order !== "",
    asModifiedFlag: item.Query_Object_Flag === "1",
    llmFlag: item.LLM_flag === "True",

    // Structural components
    joins: Array.isArray(item.Join_Conditions) ? item.Join_Conditions : [],
    predicates: Array.isArray(item.Where_Conditions)
      ? item.Where_Conditions
      : [],
    inClauses: Array.isArray(item.IN_Conditions) ? item.IN_Conditions : [],
    whereClauses: Array.isArray(item.Where_Conditions)
      ? item.Where_Conditions
      : [],
    tableNames: Array.isArray(item.Tables) ? item.Tables : [],

    // Semantic enrichments
    features: Array.isArray(item.Features) ? item.Features : [],

    tags: Array.isArray(item.Tags) ? item.Tags : [],
    mentioned: Array.isArray(item.Mentioned) ? item.Mentioned : [],
    inferences: Array.isArray(item.Inferences) ? item.Inferences : [],
    values: Array.isArray(item.Values) ? item.Values : [],
    columnNames: Array.isArray(item.Columns) ? item.Columns : [],
    aliases: Array.isArray(item.Aliases) ? item.Aliases : [],
    medicationEnrichments: Array.isArray(item.Medication_Enrichments)
      ? item.Medication_Enrichments
      : [],
    indicationEnrichments: Array.isArray(item.Indication_Enrichments)
      ? item.Indication_Enrichments
      : [],
    procedureEnrichments: [],

    // Execution metrics
    executions: Array.isArray(item.Number_of_Executions)
      ? item.Number_of_Executions
      : [],

    // Descriptive metadata
    description: {
      main: item.description?.main || "",
      inclusionRules: item.description?.inclusionRules || "None",
      exclusionRules: item.description?.exclusionRules || "None",
    },

    // Additional parameters
    unnamed01: "", // Not present in sample data
    unnamed0: item.Unnamed_0 || "",
  }));
};

// Summary Data Interfaces
export interface BreakdownItem {
  value: string;
  cnt: number;
}

export interface SummaryData {
  overall_stats: {
    number_of_queries_from_the_client: number;
    unique_queries_from_the_client: number;
    number_of_decomposed_queries: number;
    unique_neuronIDs: number;
    unique_neuron_txt: number;
    unique_neurons: number; // Combined unique neurons count
    unique_users: number;
    time_period: string;
  };
  enrichment_stats: {
    analytics_type: number;
    business_rules: number;
    features: number;
    indication_enrichments: number;
    medication_enrichments: number;
    project_steps: number;
  };
  enrichment_breakdowns: {
    analytics_type: BreakdownItem[];
    business_rules: BreakdownItem[];
    features: BreakdownItem[];
    indication_enrichments: BreakdownItem[];
    medication_enrichments: BreakdownItem[];
    project_step: BreakdownItem[];
  };
}

// Cache for summary data
let cachedSummaryData: SummaryData | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Helper function to count items in array and return top N
const getTopItems = (items: string[], topN: number = 10): BreakdownItem[] => {
  const counts: { [key: string]: number } = {};

  items.forEach((item) => {
    if (item && item.trim()) {
      const cleanItem = item.trim().toLowerCase();
      counts[cleanItem] = (counts[cleanItem] || 0) + 1;
    }
  });

  return Object.entries(counts)
    .map(([value, cnt]) => ({ value, cnt }))
    .sort((a, b) => b.cnt - a.cnt)
    .slice(0, topN);
};

// Helper function to get unique values from array
const getUniqueCount = (items: string[]): number => {
  const uniqueItems = new Set(items.filter((item) => item && item.trim()));
  return uniqueItems.size;
};

// Helper function to get date range from data
const getDateRange = (queries: Query[]): string => {
  const dates = queries
    .map((q) => q.startDate || q.RunTimestamp)
    .filter((date) => date)
    .sort();

  if (dates.length === 0) return "No date range available";

  const startDate = dates[0];
  const endDate = dates[dates.length - 1];

  return `from ${startDate} to ${endDate}`;
};

// Main function to calculate summary data from queries
export const calculateSummaryData = async (): Promise<SummaryData> => {
  // Check cache first
  const now = Date.now();
  if (cachedSummaryData && now - cacheTimestamp < CACHE_DURATION) {
    return cachedSummaryData;
  }

  try {
    // Import queries data directly
    const queriesData = await import("@/queries.json");
    const rawData = queriesData.default;

    console.log("Raw data sample:", rawData.slice(0, 2)); // Debug log

    // Calculate overall stats directly from raw data
    const totalQueries = rawData.length;

    // Use Neuron_ID for unique queries as requested
    const uniqueQueries = new Set(
      rawData.map((item: any) => item.Neuron_ID).filter(Boolean)
    ).size;

    const decomposedQueries = rawData.filter(
      (item: any) => item.Query_Type !== "MAIN"
    ).length;

    // Calculate unique neurons from Neuron_Text only as requested
    const uniqueNeuronTexts = new Set(
      rawData.map((item: any) => item.Neuron_Text).filter(Boolean)
    ).size;
    const uniqueNeurons = uniqueNeuronTexts; // Use only unique neuron texts

    // Keep separate counts for display purposes
    const uniqueNeuronIds = new Set(
      rawData.map((item: any) => item.Neuron_ID).filter(Boolean)
    ).size;
    const uniqueUsers = new Set(
      rawData.map((item: any) => item.Username).filter(Boolean)
    ).size;

    // Collect all enrichment arrays directly from raw data
    const allFeatures: string[] = [];
    const allMedicationEnrichments: string[] = [];
    const allIndicationEnrichments: string[] = [];
    const allAnalyticsTypes: string[] = [];
    const allBusinessRules: string[] = [];
    const allProjectSteps: string[] = [];

    rawData.forEach((item: any) => {
      // Features
      if (Array.isArray(item.Features)) {
        allFeatures.push(...item.Features.filter(Boolean));
      }

      // Medication enrichments
      if (Array.isArray(item.Medication_Enrichments)) {
        allMedicationEnrichments.push(
          ...item.Medication_Enrichments.filter(Boolean)
        );
      }

      // Indication enrichments
      if (Array.isArray(item.Indication_Enrichments)) {
        allIndicationEnrichments.push(
          ...item.Indication_Enrichments.filter(Boolean)
        );
      }

      // Analytics types - handle both string and array formats
      if (item.analytics_type) {
        if (typeof item.analytics_type === "string") {
          // Handle string format like "['medication persistence']"
          try {
            const parsed = JSON.parse(item.analytics_type.replace(/'/g, '"'));
            if (Array.isArray(parsed)) {
              allAnalyticsTypes.push(...parsed.filter(Boolean));
            }
          } catch {
            allAnalyticsTypes.push(item.analytics_type);
          }
        } else if (Array.isArray(item.analytics_type)) {
          allAnalyticsTypes.push(...item.analytics_type.filter(Boolean));
        }
      }

      // Business rules
      if (Array.isArray(item.business_rules)) {
        allBusinessRules.push(...item.business_rules.filter(Boolean));
      }

      // Project steps
      if (Array.isArray(item.project_step)) {
        allProjectSteps.push(...item.project_step.filter(Boolean));
      }
    });

    console.log("Collected data:", {
      features: allFeatures.length,
      medications: allMedicationEnrichments.length,
      indications: allIndicationEnrichments.length,
      analyticsTypes: allAnalyticsTypes.length,
      businessRules: allBusinessRules.length,
      projectSteps: allProjectSteps.length,
    }); // Debug log

    // Get date range from actual data
    const dates = rawData
      .map((item: any) => item.Query_Start_Date || item.Date_Timestamp)
      .filter(Boolean)
      .sort();

    const dateRange =
      dates.length > 0
        ? `from ${dates[0]} to ${dates[dates.length - 1]}`
        : "No date range available";

    // Build summary data
    const summaryData: SummaryData = {
      overall_stats: {
        number_of_queries_from_the_client: totalQueries,
        unique_queries_from_the_client: uniqueQueries,
        number_of_decomposed_queries: decomposedQueries,
        unique_neuronIDs: uniqueNeuronIds,
        unique_neuron_txt: uniqueNeuronTexts,
        unique_neurons: uniqueNeurons, // Add the combined unique neurons count
        unique_users: uniqueUsers,
        time_period: dateRange,
      },
      enrichment_stats: {
        analytics_type: getUniqueCount(allAnalyticsTypes),
        business_rules: getUniqueCount(allBusinessRules),
        features: getUniqueCount(allFeatures),
        indication_enrichments: getUniqueCount(allIndicationEnrichments),
        medication_enrichments: getUniqueCount(allMedicationEnrichments),
        project_steps: getUniqueCount(allProjectSteps),
      },
      enrichment_breakdowns: {
        analytics_type: getTopItems(allAnalyticsTypes, 5),
        business_rules: getTopItems(allBusinessRules, 5),
        features: getTopItems(allFeatures, 6),
        indication_enrichments: getTopItems(allIndicationEnrichments, 6),
        medication_enrichments: getTopItems(allMedicationEnrichments, 6),
        project_step: getTopItems(allProjectSteps, 7),
      },
    };

    console.log("Final summary data:", summaryData); // Debug log

    // Cache the result
    cachedSummaryData = summaryData;
    cacheTimestamp = now;

    return summaryData;
  } catch (error) {
    console.error("Error calculating summary data:", error);

    // Return fallback demo data if calculation fails
    return {
      overall_stats: {
        number_of_queries_from_the_client: 0,
        unique_queries_from_the_client: 0,
        number_of_decomposed_queries: 0,
        unique_neuronIDs: 0,
        unique_neuron_txt: 0,
        unique_neurons: 0,
        unique_users: 0,
        time_period: "No data available",
      },
      enrichment_stats: {
        analytics_type: 0,
        business_rules: 0,
        features: 0,
        indication_enrichments: 0,
        medication_enrichments: 0,
        project_steps: 0,
      },
      enrichment_breakdowns: {
        analytics_type: [],
        business_rules: [],
        features: [],
        indication_enrichments: [],
        medication_enrichments: [],
        project_step: [],
      },
    };
  }
};

// Function to clear cache (useful for testing or manual refresh)
export const clearSummaryCache = (): void => {
  cachedSummaryData = null;
  cacheTimestamp = 0;
};